import React, { useState, useRef, useEffect } from "react";
import styles from "./SettingsPage.module.css";
import { useAuth } from "../../../components/Auth/AuthProvider";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useThemedIcon } from "../../../hooks/useThemedIcon";
import { useTheme } from "../../../App";
import { auth } from "../../../firebase/config";
import { storage } from "../../../firebase/config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../../../firebase/config";
import { doc, updateDoc } from "firebase/firestore";
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signOut,
  verifyBeforeUpdateEmail,
} from "firebase/auth";

import visibleIconLight from "../../../assets/visible_light.svg";
import visibleIconDark from "../../../assets/visible_dark.svg";
import notVisibleIconLight from "../../../assets/not_visible_light.svg";
import notVisibleIconDark from "../../../assets/not_visible_dark.svg";
import warningIconLight from "../../../assets/warning_light.svg";
import warningIconDark from "../../../assets/warning_dark.svg";
import checkIconLight from "../../../assets/check_light.svg";
import checkIconDark from "../../../assets/check_dark.svg";
import billingLoadLight from "../../../assets/billingLoad_light.svg";
import billingLoadDark from "../../../assets/billingLoad_dark.svg";
import sendIconLight from "../../../assets/external_light.svg";
import sendIconDark from "../../../assets/external_dark.svg";
import lightIconActiveLightTheme from "../../../assets/light_icon_active_light.svg";
import lightIconActiveDarkTheme from "../../../assets/light_icon_active_dark.svg";
import lightIconInactiveLightTheme from "../../../assets/light_icon_inactive_light.svg";
import lightIconInactiveDarkTheme from "../../../assets/light_icon_inactive_dark.svg";
import darkIconActiveLightTheme from "../../../assets/dark_icon_active_light.svg";
import darkIconActiveDarkTheme from "../../../assets/dark_icon_active_dark.svg";
import darkIconInactiveLightTheme from "../../../assets/dark_icon_inactive_light.svg";
import darkIconInactiveDarkTheme from "../../../assets/dark_icon_inactive_dark.svg";
import systemIconActiveLightTheme from "../../../assets/system_icon_active_light.svg";
import systemIconActiveDarkTheme from "../../../assets/system_icon_active_dark.svg";
import systemIconInactiveLightTheme from "../../../assets/system_icon_inactive_light.svg";
import systemIconInactiveDarkTheme from "../../../assets/system_icon_inactive_dark.svg";

interface SettingsPageProps {
  onClose: () => void;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ onClose }) => {
  const { getIcon } = useThemedIcon();
  const { user, userProfile, updateUserProfile } = useAuth();
  const { theme: activeTheme, setTheme: setActiveTheme } = useTheme();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const visibleIcon = getIcon(visibleIconLight, visibleIconDark);
  const notVisibleIcon = getIcon(notVisibleIconLight, notVisibleIconDark);
  const warningIcon = getIcon(warningIconLight, warningIconDark);
  const checkIcon = getIcon(checkIconLight, checkIconDark);
  const billingLoad = getIcon(billingLoadLight, billingLoadDark);
  const sendIcon = getIcon(sendIconLight, sendIconDark);

  // Profile
  const [profileImage, setProfileImage] = useState<string | null>(
    userProfile?.photoURL || user?.photoURL || null,
  );
  const [editedName, setEditedName] = useState(
    userProfile?.fullName || user?.displayName || "",
  );
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // Email
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editedEmail, setEditedEmail] = useState("");
  const [emailConfirmPassword, setEmailConfirmPassword] = useState("");
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);

  // Password
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Delete
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Billing
  const [isBillingLoading, setIsBillingLoading] = useState(false);

  // Feedback
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (userProfile?.photoURL) setProfileImage(userProfile.photoURL);
  }, [userProfile?.photoURL]);

  const getAuthMethod = () => {
    if (!user?.providerData?.length) return "email";
    const pid = user.providerData[0].providerId;
    if (pid === "google.com") return "google";
    if (pid === "microsoft.com") return "microsoft";
    if (pid === "apple.com") return "apple";
    return "email";
  };
  const authMethod = getAuthMethod();
  const isEmailAuth = authMethod === "email";

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const getActualDisplayTheme = (): "light" | "dark" =>
    activeTheme === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : (activeTheme as "light" | "dark");

  const getLightModeIcon = () => {
    const active = activeTheme === "light";
    return getActualDisplayTheme() === "light"
      ? active
        ? lightIconActiveLightTheme
        : lightIconInactiveLightTheme
      : active
        ? lightIconActiveDarkTheme
        : lightIconInactiveDarkTheme;
  };
  const getDarkModeIcon = () => {
    const active = activeTheme === "dark";
    return getActualDisplayTheme() === "light"
      ? active
        ? darkIconActiveLightTheme
        : darkIconInactiveLightTheme
      : active
        ? darkIconActiveDarkTheme
        : darkIconInactiveDarkTheme;
  };
  const getSystemModeIcon = () => {
    const active = activeTheme === "system";
    return getActualDisplayTheme() === "light"
      ? active
        ? systemIconActiveLightTheme
        : systemIconInactiveLightTheme
      : active
        ? systemIconActiveDarkTheme
        : systemIconInactiveDarkTheme;
  };

  const handleThemeToggle = async (theme: "light" | "dark" | "system") => {
    document.documentElement.classList.add("theme-transitioning");
    setActiveTheme(theme);
    setTimeout(
      () => document.documentElement.classList.remove("theme-transitioning"),
      0,
    );
    if (userProfile && updateUserProfile) {
      try {
        await updateUserProfile({ themePreference: theme });
      } catch {}
    }
  };

  const getUserAvatar = () => {
    const photoURL = profileImage || userProfile?.photoURL || user?.photoURL;
    if (photoURL) {
      const trimmed = photoURL.trim();
      const invalid =
        !trimmed ||
        trimmed === "null" ||
        trimmed === "undefined" ||
        trimmed.includes("/avatar_default") ||
        trimmed.includes("default-avatar") ||
        trimmed.includes("blank-profile") ||
        trimmed.includes("s96-c/photo.jpg") ||
        trimmed.includes("googleusercontent.com/a-/default") ||
        trimmed.includes("lh3.googleusercontent.com/a/default") ||
        trimmed.includes("lh3.googleusercontent.com/a-/");
      if (!invalid) return { type: "image" as const, value: trimmed };
    }
    const name = userProfile?.fullName || user?.displayName || "User";
    return { type: "initials" as const, value: name.charAt(0).toUpperCase() };
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file?.type.startsWith("image/")) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("Image size must be less than 5MB");
      return;
    }
    try {
      setIsUploadingPhoto(true);
      const storageRef = ref(
        storage,
        `profilePhotos/${user!.uid}/${Date.now()}_${file.name}`,
      );
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setProfileImage(url);
      if (user && updateUserProfile) await updateUserProfile({ photoURL: url });
      setSuccess("Profile photo updated.");
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError("Failed to update photo.");
      setProfileImage(userProfile?.photoURL || user?.photoURL || null);
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleNameChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setEditedName(val);
    if (val.trim() && updateUserProfile) {
      try {
        await updateUserProfile({
          fullName: val.trim(),
          displayName: val.trim(),
        });
      } catch {}
    }
  };

  const handleEmailSave = async () => {
    if (!auth.currentUser || !editedEmail.trim() || !emailConfirmPassword)
      return;
    try {
      setIsSavingEmail(true);
      setError(null);
      const cred = EmailAuthProvider.credential(
        auth.currentUser.email!,
        emailConfirmPassword,
      );
      await reauthenticateWithCredential(auth.currentUser, cred);
      await verifyBeforeUpdateEmail(auth.currentUser, editedEmail.trim(), {
        url: window.location.origin,
        handleCodeInApp: true,
      });
      setPendingEmail(editedEmail.trim());
      setIsEditingEmail(false);
      setEmailConfirmPassword("");
      setEditedEmail("");
    } catch (err: any) {
      if (err.code === "auth/wrong-password")
        setError("Current password is incorrect.");
      else if (err.code === "auth/email-already-in-use")
        setError("Email already in use.");
      else if (err.code === "auth/invalid-email")
        setError("Invalid email address.");
      else setError("Failed to send verification email.");
    } finally {
      setIsSavingEmail(false);
    }
  };

  const handleResendVerification = async () => {
    if (!pendingEmail || !auth.currentUser) return;
    try {
      setIsResendingEmail(true);
      setError(null);
      await verifyBeforeUpdateEmail(auth.currentUser, pendingEmail, {
        url: window.location.origin,
        handleCodeInApp: true,
      });
      setSuccess("Verification link resent.");
    } catch {
      setError("Failed to resend email.");
    } finally {
      setIsResendingEmail(false);
    }
  };

  const handlePasswordSave = async () => {
    if (!auth.currentUser || !currentPassword || newPassword.length < 8) return;
    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match.");
      return;
    }
    try {
      setIsSavingPassword(true);
      setError(null);
      const cred = EmailAuthProvider.credential(
        auth.currentUser.email!,
        currentPassword,
      );
      await reauthenticateWithCredential(auth.currentUser, cred);
      await updatePassword(auth.currentUser, newPassword);
      setIsEditingPassword(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      setSuccess("Password changed successfully.");
    } catch (err: any) {
      if (err.code === "auth/wrong-password")
        setError("Current password is incorrect.");
      else if (err.code === "auth/weak-password")
        setError("Password too weak.");
      else setError("Failed to update password.");
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!auth.currentUser) return;
    setIsDeleting(true);
    try {
      const fns = getFunctions();
      await httpsCallable(fns, "deleteUserAccount")();
      await signOut(auth);
    } catch (err: any) {
      if (err.code === "auth/requires-recent-login")
        setError("Please log out and back in before deleting.");
      else setError("Failed to delete account.");
      setShowDeleteConfirm(false);
      setIsDeleting(false);
    }
  };

  const handleBillingClick = async () => {
    setIsBillingLoading(true);
    sessionStorage.setItem("billing_return_url", window.location.href);
    try {
      const fns = getFunctions();
      const { data } = await httpsCallable(
        fns,
        "createCustomerPortalSession",
      )({
        returnUrl: window.location.origin + "/?billing_return=true",
      });
      window.location.href = (data as any).url;
    } catch {
      setIsBillingLoading(false);
    }
  };

  const hasActiveSubscription =
    (userProfile as any)?.subscriptionStatus === "active" &&
    (userProfile as any)?.subscriptionTier !== "free" &&
    (userProfile as any)?.subscriptionTier !== undefined;

  const hasStripeCustomerId = !!(userProfile as any)?.stripeCustomerId;
  const avatar = getUserAvatar();

  const getAuthProviderBadge = () => {
    if (authMethod === "google")
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          style={{ flexShrink: 0 }}
        >
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      );
    if (authMethod === "microsoft")
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          style={{ flexShrink: 0 }}
        >
          <path fill="#F25022" d="M1 1h10v10H1z" />
          <path fill="#00A4EF" d="M13 1h10v10H13z" />
          <path fill="#7FBA00" d="M1 13h10v10H1z" />
          <path fill="#FFB900" d="M13 13h10v10H13z" />
        </svg>
      );
    if (authMethod === "apple")
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="var(--color-black)"
          style={{ flexShrink: 0 }}
        >
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
      );
    return null;
  };

  if (showDeleteConfirm) {
    return (
      <div className={styles.container}>
        <div className={styles.inner}>
          <h2 className={styles.title}>Are you sure?</h2>
          <p className={styles.deleteConfirmDescription}>
            This will permanently delete your account and all associated data.
            You'll immediately lose access, and this action cannot be undone.
          </p>
          {error && <p className={styles.errorText}>{error}</p>}
          <div className={styles.deleteConfirmButtons}>
            <button
              className={styles.deleteConfirmButton}
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting
                ? "Deleting your account..."
                : "Yes, delete my account"}
            </button>
            <button
              className={styles.cancelButton}
              onClick={() => {
                setShowDeleteConfirm(false);
                setError(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <h2 className={styles.title}>Settings</h2>

        {error && <p className={styles.errorText}>{error}</p>}
        {success && <p className={styles.successText}>{success}</p>}

        {/* Photo */}
        <div className={styles.photoSection}>
          <div className={styles.avatarCircle}>
            {avatar.type === "image" ? (
              <img
                src={avatar.value}
                alt="Profile"
                referrerPolicy="no-referrer"
                className={styles.avatarImage}
              />
            ) : (
              <span className={styles.avatarInitials}>{avatar.value}</span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <button
            className={styles.changePhotoButton}
            onClick={handleUploadClick}
            disabled={isUploadingPhoto}
          >
            {isUploadingPhoto ? "Uploading..." : "Upload"}
          </button>
        </div>

        {/* Name */}
        <div className={styles.section}>
          <label className={styles.label}>Name</label>
          <input
            type="text"
            value={editedName}
            onChange={handleNameChange}
            className={styles.input}
            placeholder="Enter your full name"
          />
        </div>

        {/* Email — SSO provider */}
        {!isEmailAuth && (
          <div className={styles.section}>
            <label className={styles.label}>Email</label>
            <div className={styles.displayBoxSso}>
              <span className={styles.providerIcon}>
                {getAuthProviderBadge()}
              </span>
              <span>{user?.email}</span>
            </div>
          </div>
        )}

        {/* Email — email auth */}
        {isEmailAuth && !isEditingEmail && !pendingEmail && (
          <div className={styles.section}>
            <label className={styles.label}>Email</label>
            <div className={styles.displayBox}>{user?.email}</div>
            <button
              className={styles.changeLink}
              onClick={() => {
                setIsEditingEmail(true);
                setError(null);
              }}
            >
              Change email
            </button>
          </div>
        )}

        {isEmailAuth && isEditingEmail && (
          <div className={styles.section}>
            <label className={styles.label}>Current email</label>
            <div className={styles.displayBox}>{user?.email}</div>
            <label className={styles.label} style={{ marginTop: "0.75rem" }}>
              New email
            </label>
            <input
              type="email"
              value={editedEmail}
              onChange={(e) => {
                setEditedEmail(e.target.value);
                setError(null);
              }}
              className={styles.input}
              placeholder="name@example.com"
              autoFocus
            />
            <label className={styles.label} style={{ marginTop: "0.75rem" }}>
              Password
            </label>
            <input
              type="password"
              value={emailConfirmPassword}
              onChange={(e) => {
                setEmailConfirmPassword(e.target.value);
                setError(null);
              }}
              className={styles.input}
              placeholder="Confirm with your password"
            />
            <button
              className={styles.saveButton}
              onClick={handleEmailSave}
              disabled={
                !isValidEmail(editedEmail) ||
                !emailConfirmPassword ||
                isSavingEmail
              }
            >
              {isSavingEmail ? "Saving..." : "Save changes"}
            </button>
          </div>
        )}

        {isEmailAuth && pendingEmail && (
          <div className={styles.section}>
            <label className={styles.label}>Email</label>
            <p className={styles.pendingText}>
              We've sent a verification link to your new email. Click the link
              to confirm your new address.
            </p>
            <button
              className={styles.changeLink}
              onClick={handleResendVerification}
              disabled={isResendingEmail}
            >
              {isResendingEmail ? "Resending..." : "Resend link"}
            </button>
          </div>
        )}

        {/* Password */}
        {isEmailAuth && !isEditingPassword && (
          <div className={styles.section}>
            <label className={styles.label}>Password</label>
            <div className={styles.displayBox}>••••••••</div>
            <button
              className={styles.changeLink}
              onClick={() => {
                setIsEditingPassword(true);
                setError(null);
              }}
            >
              Change password
            </button>
          </div>
        )}

        {isEmailAuth && isEditingPassword && (
          <div className={styles.section}>
            <label className={styles.label}>Current password</label>
            <div className={styles.passwordRow}>
              <input
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  setError(null);
                }}
                className={styles.input}
                placeholder="••••••••"
                autoFocus
              />
              <button
                className={styles.eyeButton}
                onClick={() => setShowCurrentPassword((p) => !p)}
              >
                <img
                  src={showCurrentPassword ? visibleIcon : notVisibleIcon}
                  alt=""
                  className={styles.eyeIcon}
                />
              </button>
            </div>

            <label className={styles.label} style={{ marginTop: "0.75rem" }}>
              New password
            </label>
            <div className={styles.passwordRow}>
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setError(null);
                }}
                className={styles.input}
                placeholder="8+ characters"
              />
              {newPassword.length > 0 && (
                <img
                  src={newPassword.length >= 8 ? checkIcon : warningIcon}
                  alt=""
                  className={styles.statusIcon}
                />
              )}
              <button
                className={styles.eyeButton}
                onClick={() => setShowNewPassword((p) => !p)}
              >
                <img
                  src={showNewPassword ? visibleIcon : notVisibleIcon}
                  alt=""
                  className={styles.eyeIcon}
                />
              </button>
            </div>

            <label className={styles.label} style={{ marginTop: "0.75rem" }}>
              Confirm new password
            </label>
            <div className={styles.passwordRow}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmNewPassword}
                onChange={(e) => {
                  setConfirmNewPassword(e.target.value);
                  setError(null);
                }}
                className={styles.input}
                placeholder="8+ characters"
              />
              {confirmNewPassword.length > 0 && (
                <img
                  src={
                    confirmNewPassword.length >= 8 &&
                    confirmNewPassword === newPassword
                      ? checkIcon
                      : warningIcon
                  }
                  alt=""
                  className={styles.statusIcon}
                />
              )}
              <button
                className={styles.eyeButton}
                onClick={() => setShowConfirmPassword((p) => !p)}
              >
                <img
                  src={showConfirmPassword ? visibleIcon : notVisibleIcon}
                  alt=""
                  className={styles.eyeIcon}
                />
              </button>
            </div>

            <button
              className={styles.saveButton}
              onClick={handlePasswordSave}
              disabled={
                !currentPassword ||
                newPassword.length < 8 ||
                confirmNewPassword !== newPassword ||
                isSavingPassword
              }
            >
              {isSavingPassword ? "Saving..." : "Save changes"}
            </button>
          </div>
        )}

        {/* Delete account */}
        <div className={styles.section}>
          <p className={styles.deleteDescription}>
            Deleting your Noah account will immediately revoke access to all
            projects and permanently delete your data. This action can't be
            undone.
          </p>
          {hasActiveSubscription ? (
            <div className={styles.subscriptionWarning}>
              <img
                src={warningIcon}
                alt=""
                style={{ width: "0.875rem", height: "0.875rem", flexShrink: 0 }}
              />
              <span>
                You have an active{" "}
                <strong>{(userProfile as any)?.subscriptionTier}</strong>{" "}
                subscription. Please cancel it before deleting your account.{" "}
                <button
                  className={styles.inlineLink}
                  onClick={handleBillingClick}
                >
                  Manage subscription
                </button>
              </span>
            </div>
          ) : (
            <button
              className={styles.deleteButton}
              onClick={() => setShowDeleteConfirm(true)}
            >
              Permanently delete account
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
